import React from 'react';
import { Calendar, MapPin, Ticket, Handshake, Download, Mic2, Quote } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import ReportViewer from '../components/ReportViewer';
import LeadForm from '../components/LeadForm';
import { useContent } from '../context/ContentContext';

const ACCENT = '#D01919';
const REPORT = 'https://customer-assets-lqy194kg.emergentagent.net/job_enersider-revamp/artifacts/kslvkqdk_Post%20Show%20Report%20ABC_Energy%20Dialogues.pdf';
const HERO = 'https://images.unsplash.com/photo-1769798643655-e0f10f62c3fd?crop=entropy&cs=srgb&fm=jpg&q=85';

const dialogues = [
  ['Climate Action Dialogues', 'Chintan Research Foundation', 'Decarbonisation without disruption.'],
  ['Natural Gas Dialogues', 'Natural Gas Society', 'City gas, LNG and gas as a transition fuel.'],
  ['Green Hydrogen Dialogues', 'GH2 India', 'From policy intent to bankable projects.'],
  ['Net Zero Energy Dialogues', 'NETRA', 'Scaling cleantech manufacturing & supply chains.'],
  ['Bioenergy Dialogues', 'Indian Biogas Association', 'Biofuels, waste-to-energy and rural economies.'],
  ['Inaugural Session', 'Enersider', 'Setting the agenda for the year in energy.'],
];

function initials(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

export default function ABC() {
  const { abcSpeakers, abcSponsors, eventsData } = useContent();
  const hero = eventsData?.conclave?.image || HERO;
  return (
    <div className="pt-28 md:pt-36 pb-24">
      {/* Hero */}
      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <div className="relative rounded-3xl overflow-hidden text-white">
            <img src={hero} alt="ABC" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, #111111f2, #1a1a1acc 55%, #11111199)' }} />
            <div className="relative p-10 md:p-16">
              <Reveal>
                <p className="eyebrow mb-3" style={{ color: ACCENT }}>Annual Business Conclave</p>
                <h1 className="font-display text-5xl md:text-7xl leading-[0.98]">The Energy Dialogues</h1>
                <p className="text-white/85 text-lg mt-5 max-w-2xl">A full year in the making — Enersider's flagship convening of six sector dialogues, bringing India's policymakers, developers, financiers and technologists onto one stage.</p>
                <div className="flex flex-wrap gap-5 mt-6 text-sm">
                  <span className="inline-flex items-center gap-1.5"><Calendar size={16} /> Next edition: March 2027</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={16} /> New Delhi</span>
                </div>
                <div className="flex flex-wrap gap-3 mt-8">
                  <a href="#delegate" data-testid="abc-delegate-cta" className="inline-flex items-center gap-2 bg-white text-[#111111] font-semibold px-6 py-3.5 rounded-full hover:bg-[#D01919] hover:text-white transition-colors"><Ticket size={18} /> Delegate Registration</a>
                  <a href="#partnership" data-testid="abc-partnership-cta" className="inline-flex items-center gap-2 border border-white/50 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/10"><Handshake size={18} /> Partnership</a>
                  <a href="#abc-report" className="inline-flex items-center gap-2 border border-white/50 text-white font-medium px-6 py-3.5 rounded-full hover:bg-white/10"><Download size={18} /> Post-show Report</a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[['6', 'Sector dialogues'], ['33', 'Speakers'], ['600+', 'Delegates'], ['5', 'Association partners']].map(([v, l]) => (
            <div key={l} className="border-l-2 pl-5" style={{ borderColor: ACCENT }}>
              <p className="font-display text-4xl md:text-5xl" style={{ color: ACCENT }}>{v}</p>
              <p className="text-sm text-[#666666] mt-1">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Dialogues */}
      <section className="section-pad py-8">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display text-3xl md:text-4xl mb-8">Six dialogues, one conclave</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {dialogues.map((d, i) => (
              <Reveal key={d[0]} delay={i * 60}>
                <div className="bg-[#f4f4f4] rounded-2xl p-6 h-full border-t-4" style={{ borderColor: ACCENT }}>
                  <h3 className="font-display text-xl">{d[0]}</h3>
                  <p className="text-xs uppercase tracking-wide text-[#666666] mt-1">with {d[1]}</p>
                  <p className="text-sm mt-3">{d[2]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Speakers (CMS-managed photos) */}
      {abcSpeakers.length > 0 && (
        <section className="section-pad py-14">
          <div className="max-w-[1500px] mx-auto">
            <div className="flex items-center gap-2 mb-8"><Mic2 size={20} style={{ color: ACCENT }} /><h2 className="font-display text-3xl md:text-4xl">Speakers</h2></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5" data-testid="abc-speakers">
              {abcSpeakers.map((s, i) => (
                <Reveal key={s.id || i} delay={(i % 6) * 40}>
                  <div className="text-center">
                    <div className="aspect-square rounded-2xl overflow-hidden bg-[#111111] flex items-center justify-center">
                      {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : <span className="font-display text-3xl text-white/70">{initials(s.name)}</span>}
                    </div>
                    <p className="font-medium mt-3 leading-tight text-sm">{s.name}</p>
                    {s.role ? <p className="text-xs text-[#666666]">{s.role}</p> : null}
                    <p className="text-xs" style={{ color: ACCENT }}>{s.org}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="text-xs text-[#666666] mt-5">Speaker photos are managed from the Enersider admin. Representative line-up from the last edition.</p>
          </div>
        </section>
      )}

      {/* Sponsors / partners (CMS-managed logos) */}
      {abcSponsors.length > 0 && (
        <section className="section-pad py-10 bg-[#111111] text-white">
          <div className="max-w-[1500px] mx-auto">
            <p className="eyebrow mb-6" style={{ color: ACCENT }}>Partners & sponsors</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="abc-sponsors">
              {abcSponsors.map((sp, i) => (
                <Reveal key={sp.id || i} delay={(i % 6) * 40}>
                  <div className="rounded-2xl bg-white/5 border border-white/10 h-24 flex items-center justify-center p-4 text-center">
                    {sp.logo ? <img src={sp.logo} alt={sp.name} className="max-h-14 max-w-full object-contain" /> : <span className="font-display text-base text-white/80">{sp.name}</span>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <ReportViewer url={eventsData?.conclave?.reportPdf || REPORT} accent={ACCENT} bg="linear-gradient(135deg, #111111, #2a0a0a)" testid="abc-report"
        eyebrow={eventsData?.conclave?.reportEdition || 'Edition 1 · March 2026'} title="Post-show report"
        nextAt={eventsData?.conclave?.nextEditionAt} nextLabel={eventsData?.conclave?.nextEditionLabel || 'Edition 2 begins in'}
        intro="Flip through what happened at the Annual Business Conclave — the agenda, speakers, partners and highlights." />

      {/* Report */}
      <section id="report" className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-3xl p-10 text-white" style={{ background: 'linear-gradient(135deg, #111111, #2a0a0a)' }}>
              <Quote size={36} style={{ color: ACCENT }} />
              <p className="font-display text-2xl md:text-3xl mt-3 leading-snug">See who was in the room.</p>
              <p className="text-white/80 mt-3">Download the official post-show report — the agenda, speakers, partners and highlights from the last Annual Business Conclave.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm type="ABC Post-show Report Download" recipient="partnerships@enersider.com" accent={ACCENT}
              heading="Download the post-show report" cta="Get the report" downloadUrl={REPORT}
              extraFields={[{ k: 'interest', l: 'Also interested in (delegate / partnership)', full: true }]} />
          </Reveal>
        </div>
      </section>

      {/* Delegate + Partnership */}
      <section className="section-pad py-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-8 items-start">
          <div id="delegate" className="scroll-mt-28" data-testid="abc-delegate-section">
            <p className="eyebrow text-[#D01919] mb-3">Delegate registration · March 2027</p>
            <h2 className="font-display text-3xl md:text-4xl mb-6">Book your delegate pass</h2>
            <LeadForm type="ABC Delegate Registration" recipient="zeba@enersider.com" cc={null} accent={ACCENT} cta="Register as delegate"
              note={<div className="mb-6 rounded-2xl bg-[#111111] text-white p-5 flex items-end justify-between gap-4" data-testid="abc-delegate-price"><div><p className="text-xs uppercase tracking-wider text-white/60">Delegate pass</p><p className="font-display text-3xl mt-1">INR 11,800</p></div><p className="text-sm text-white/70 text-right">per delegate<br />inclusive of taxes</p></div>}
              extraFields={[
                { k: 'designation', l: 'Designation' },
                { k: 'delegates', l: 'Number of delegates', type: 'number', req: true },
                { k: 'message', l: 'Delegate names / notes', area: true, full: true },
              ]} />
            <p className="text-xs text-neutral-500 mt-3">No online payment — our team will contact you to confirm your registration and share payment details.</p>
          </div>
          <div id="partnership" className="scroll-mt-28" data-testid="abc-partnership-section">
            <p className="eyebrow text-[#D01919] mb-3">Partner with ABC</p>
            <h2 className="font-display text-3xl md:text-4xl mb-6">Partnership enquiry</h2>
            <LeadForm type="ABC Partnership Enquiry" recipient="partnerships@enersider.com" accent={ACCENT} cta="Send partnership enquiry"
              extraFields={[
                { k: 'designation', l: 'Designation' },
                { k: 'partnership', l: 'Partnership interest', options: ['Title / Presenting partner', 'Session partner', 'Exhibition', 'Knowledge partner', 'Media partner', 'Other'], req: true },
                { k: 'message', l: 'Message', area: true, full: true },
              ]} />
          </div>
        </div>
      </section>

      {/* Interest */}
      <section className="section-pad py-10">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div>
              <p className="eyebrow mb-3" style={{ color: ACCENT }}>March 2027</p>
              <h2 className="font-display text-4xl md:text-5xl">Register your interest</h2>
              <p className="text-[#666666] mt-4 max-w-md">Be first to hear about delegate passes, speaking slots and sponsorship for the next Annual Business Conclave.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <LeadForm type="ABC March 2027 Interest" recipient="partnerships@enersider.com" accent={ACCENT}
              extraFields={[{ k: 'interest', l: 'Interested in', options: ['Delegate', 'Speaking', 'Partnership'], req: true, full: true }, { k: 'message', l: 'Message', area: true, full: true }]}
              cta="Submit interest" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
